import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Spinner } from '../ui.jsx';

// System Health — read-only view of the nightly smoke-test runs + recent
// errors. Backed by /api/errors/smoke-runs and /api/errors/recent.

export function SystemHealth({ toast }) {
  const [tab, setTab] = useState('smoke');
  return <div style={{ padding: 20, maxWidth: 1100 }}>
    <div style={{ marginBottom: 14 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800 }}>🩺 System Health</h1>
      <div style={{ fontSize: 12, color: G.textMuted, marginTop: 2 }}>
        Smoke tests run every 10 min during 23:00–06:00 server time. Failures get logged here + to <code>flyctl logs</code>.
      </div>
    </div>
    <div style={{ display: 'flex', gap: 4, marginBottom: 14, borderBottom: `1px solid ${G.cardBorder}`, overflowX: 'auto' }}>
      {[
        { k: 'smoke',  label: '🧪 Smoke Runs' },
        { k: 'errors', label: '⚠️ Recent Errors' },
      ].map(t => (
        <button key={t.k} onClick={() => setTab(t.k)}
          style={{
            background: 'none', border: 'none', padding: '10px 14px', cursor: 'pointer',
            color: tab === t.k ? G.accent : G.textMuted,
            borderBottom: tab === t.k ? `2px solid ${G.accent}` : '2px solid transparent',
            fontWeight: tab === t.k ? 700 : 500, fontSize: 13, marginBottom: -1, whiteSpace: 'nowrap',
          }}>{t.label}</button>
      ))}
    </div>

    {tab === 'smoke'  && <SmokeRunsTab toast={toast}/>}
    {tab === 'errors' && <ErrorsTab toast={toast}/>}
  </div>;
}

function SmokeRunsTab({ toast }) {
  const [runs, setRuns] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try { const r = await api('/api/errors/smoke-runs'); setRuns(r.runs || []); }
    catch (e) { toast?.(e.message, 'error'); setRuns([]); }
    finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!openId) { setDetail(null); return; }
    api(`/api/errors/smoke-runs/${openId}`).then(setDetail).catch(()=>setDetail(null));
  }, [openId]);

  if (!runs) return <Spinner sz={24}/>;

  const total24h = runs.filter(r => Date.now() - new Date(r.created_at || r.started_at).getTime() < 86400000).length;
  const failed24h = runs.filter(r => (Date.now() - new Date(r.created_at || r.started_at).getTime() < 86400000) && r.failed > 0).length;

  return <>
    <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center' }}>
      <Btn sz="sm" onClick={load} disabled={refreshing}>{refreshing ? <Spinner sz={12}/> : '↻ Refresh'}</Btn>
      <div style={{ fontSize: 12, color: G.textMuted }}>
        {total24h} runs in last 24h · {failed24h === 0 ? <span style={{ color: G.green, fontWeight: 700 }}>✓ all clean</span> : <span style={{ color: G.red, fontWeight: 700 }}>{failed24h} with failures</span>}
      </div>
    </div>

    {runs.length === 0 ? <div style={{ ...styles.card, padding: 30, textAlign: 'center', color: G.textMuted }}>
      No smoke runs recorded yet. The runner fires 30s after boot, then every 10 min during 23:00–06:00.
    </div> : <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>
          {['Started', 'Result', 'Tests', 'Duration', ''].map(h => <th key={h} style={styles.th}>{h}</th>)}
        </tr></thead>
        <tbody>{runs.map(r => {
          const isGreen = r.failed === 0;
          return <tr key={r.id} onClick={() => setOpenId(r.id === openId ? null : r.id)}
            style={{ cursor: 'pointer', background: r.id === openId ? G.input : 'transparent' }}>
            <td style={styles.td}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{new Date(r.started_at).toLocaleString()}</div>
              <div style={{ fontSize: 11, color: G.textMuted }}>{timeAgo(r.started_at)}</div>
            </td>
            <td style={styles.td}>
              <Badge c={isGreen ? G.green : G.red} bg={(isGreen ? G.green : G.red) + '22'}>
                {isGreen ? '✓ PASS' : `✗ ${r.failed} FAILED`}
              </Badge>
            </td>
            <td style={styles.td}>{r.passed}/{r.total}</td>
            <td style={styles.td}>{r.duration_ms}ms</td>
            <td style={{ ...styles.td, textAlign: 'right', color: G.textMuted, fontSize: 12 }}>
              {r.id === openId ? '▲' : '▼'}
            </td>
          </tr>;
        })}</tbody>
      </table>
    </div>}

    {openId && detail && <div style={{ ...styles.card, padding: 16, marginTop: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>
        Run detail · {new Date(detail.started_at).toLocaleString()}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(detail.results || []).map((t, i) => (
          <div key={i} style={{
            padding: '8px 12px', borderRadius: 6,
            background: t.ok ? G.green + '11' : G.red + '22',
            border: `1px solid ${(t.ok ? G.green : G.red) + '44'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
              <span style={{ color: t.ok ? G.green : G.red, fontWeight: 700, fontSize: 12 }}>{t.ok ? '✓' : '✗'}</span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{t.name}</span>
              {!t.ok && t.error && <span style={{ fontSize: 12, color: G.red, opacity: .9, fontFamily: 'ui-monospace, monospace' }}>{t.error.slice(0, 200)}</span>}
            </div>
            <div style={{ fontSize: 11, color: G.textMuted, fontFamily: 'ui-monospace, monospace' }}>
              {t.status ? `HTTP ${t.status}` : ''} · {t.ms || 0}ms
            </div>
          </div>
        ))}
      </div>
    </div>}
  </>;
}

function ErrorsTab({ toast }) {
  const [events, setEvents] = useState(null);
  const [filter, setFilter] = useState('all');
  const load = async () => {
    try { const r = await api('/api/errors/recent'); setEvents(r.events || []); }
    catch (e) { toast?.(e.message, 'error'); setEvents([]); }
  };
  useEffect(() => { load(); }, []);
  if (!events) return <Spinner sz={24}/>;

  const filtered = events.filter(e => filter === 'all' || e.source === filter);
  const counts = { all: events.length, server: 0, client: 0, smoke: 0 };
  for (const e of events) counts[e.source] = (counts[e.source] || 0) + 1;

  return <>
    <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
      <Btn sz="sm" onClick={load}>↻ Refresh</Btn>
      {['all', 'server', 'client', 'smoke'].map(s => (
        <Btn key={s} sz="sm" v={filter === s ? 'primary' : 'ghost'} onClick={() => setFilter(s)}>
          {s} ({counts[s] || 0})
        </Btn>
      ))}
      <div style={{ fontSize: 12, color: G.textMuted, marginLeft: 'auto' }}>Last 24h</div>
    </div>

    {filtered.length === 0 ? <div style={{ ...styles.card, padding: 30, textAlign: 'center', color: G.green }}>
      ✓ No errors in this window.
    </div> : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {filtered.map(e => (
        <div key={e.id} style={{ ...styles.card, padding: 12, borderLeft: `3px solid ${sourceColor(e.source)}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Badge c={sourceColor(e.source)} bg={sourceColor(e.source) + '22'}>{e.source}</Badge>
              {e.kind && <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: G.textMuted }}>{e.kind}</span>}
              {e.status ? <Badge c={G.textMuted} bg={G.input}>HTTP {e.status}</Badge> : null}
            </div>
            <div style={{ fontSize: 11, color: G.textMuted }}>{timeAgo(e.created_at)}</div>
          </div>
          <div style={{ fontSize: 13, color: G.text, lineHeight: 1.4 }}>{e.message}</div>
          {e.path && <div style={{ fontSize: 11, color: G.textDim, fontFamily: 'ui-monospace, monospace', marginTop: 4 }}>{e.method || ''} {e.path}</div>}
        </div>
      ))}
    </div>}
  </>;
}

function sourceColor(s) {
  if (s === 'server') return G.red;
  if (s === 'client') return G.orange;
  if (s === 'smoke')  return G.yellow;
  return G.textMuted;
}

function timeAgo(iso) {
  if (!iso) return '';
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h ago`;
  return `${Math.floor(secs/86400)}d ago`;
}
